import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    {
      name: 'Services',
      href: '/services',
      dropdown: [
        { name: 'All Services', href: '/services' },
        { name: 'Hardware Sales', href: '/hardware-sales' },
        { name: 'Software Licensing', href: '/software-licensing' },
      ]
    },
    { name: 'Capabilities', href: '/capabilities' },
    { name: 'AI Defense', href: '/adaptive-ai-defense', highlight: true },
    { name: 'Team', href: '/team' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isServicesActive = () => {
    return location.pathname === '/services' ||
           location.pathname === '/hardware-sales' ||
           location.pathname === '/software-licensing';
  };
  const isHighlighted = (item: any) => item.highlight;

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container-max">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="/FullLogo_Transparent.png"
              alt="TyrantShield Technologies LLC - Premier IT Company & Cybersecurity Provider in Cheyenne, Wyoming"
              className="h-20 w-auto object-contain"
            />
            <span className="text-black font-bold text-2xl" style={{ textShadow: '2px 2px 4px rgb(37 99 235)' }}>
              TyrantShield Technologies LLC.
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              item.dropdown ? (
                <div key={item.name} className="relative">
                  <button
                    onClick={() => setServicesOpen(!servicesOpen)}
                    className={`font-medium transition-colors duration-200 flex items-center space-x-1 ${
                      isServicesActive()
                        ? 'text-blue-600 border-b-2 border-blue-600 pb-1'
                        : 'text-slate-700 hover:text-blue-600'
                    }`}
                  >
                    <span>{item.name}</span>
                    <ChevronDownIcon className="w-4 h-4" />
                  </button>
                  {servicesOpen && (
                    <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-2 z-50">
                      {item.dropdown.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.name}
                          to={dropdownItem.href}
                          className="block px-4 py-2 text-sm text-slate-700 hover:bg-gray-50 hover:text-blue-600"
                          onClick={() => setServicesOpen(false)}
                        >
                          {dropdownItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`font-medium transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'text-blue-600 border-b-2 border-blue-600 pb-1'
                      : isHighlighted(item)
                      ? 'text-blue-600 hover:text-blue-700 font-semibold'
                      : 'text-slate-700 hover:text-blue-600'
                  }`}
                >
                  {item.name}
                  {isHighlighted(item) && (
                    <span className="ml-1 px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full">
                      NEW
                    </span>
                  )}
                </Link>
              )
            ))}
            <Link
              to="/book"
              className="btn-primary"
            >
              Book Online
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-slate-700 hover:text-blue-600 focus:outline-none focus:text-blue-600"
            >
              {isOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden border-t border-gray-200">
            <div className="py-4 space-y-2">
              {navigation.map((item) => (
                item.dropdown ? (
                  <div key={item.name}>
                    <button
                      onClick={() => setServicesOpen(!servicesOpen)}
                      className={`w-full text-left px-4 py-2 font-medium transition-colors duration-200 flex items-center justify-between ${
                        isServicesActive()
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-slate-700 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDownIcon className="w-4 h-4" />
                    </button>
                    {servicesOpen && (
                      <div className="pl-4">
                        {item.dropdown.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.href}
                            className="block px-4 py-2 text-sm text-slate-600 hover:text-blue-600 hover:bg-gray-50"
                            onClick={() => {
                              setIsOpen(false);
                              setServicesOpen(false);
                            }}
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block px-4 py-2 font-medium transition-colors duration-200 ${
                      isActive(item.href)
                        ? 'text-blue-600 bg-blue-50'
                        : isHighlighted(item)
                        ? 'text-blue-600 bg-blue-50 font-semibold'
                        : 'text-slate-700 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                    {isHighlighted(item) && (
                      <span className="ml-2 px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full">
                        NEW
                      </span>
                    )}
                  </Link>
                )
              ))}
              <div className="px-4 pt-2">
                <Link
                  to="/book"
                  className="btn-primary w-full text-center block"
                  onClick={() => setIsOpen(false)}
                >
                  Book Online
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};